import { memo, useMemo, useRef } from 'react';
import AwesomeSwiper from 'react-awesome-swiper';
import { BannerItem } from '../../types';
import { getSafeLandingLink, toBannerItem } from '../../utils/landingPage';

interface Props {
  images?: Array<string | BannerItem>;
  className?: string;
  onImageReady?: () => void;
}

interface SwiperApi {
  update?: () => void;
  updateAutoHeight?: (speed?: number) => void;
}

type SwiperHandle = AwesomeSwiper & { swiper?: SwiperApi };

let carouselSequence = 0;

function LandingCarousel({ images = [], className = '', onImageReady }: Props) {
  const swiperRef = useRef<SwiperHandle | null>(null);
  const paginationClassRef = useRef('');
  if (!paginationClassRef.current) paginationClassRef.current = `landing-pagination-${++carouselSequence}`;
  const banners = useMemo(
    () => images.map(toBannerItem).filter((item) => Boolean(item.label)),
    [images],
  );
  const config = useMemo(
    () => ({
      loop: banners.length > 1,
      autoplay: banners.length > 1 ? { delay: 3000 } : false,
      autoHeight: true,
      preloadImages: true,
      updateOnImagesReady: true,
      observer: true,
      observeParents: true,
      speed: 500,
      pagination: {
        el: `.${paginationClassRef.current}`,
        bulletActiveClass: 'dot-active',
        bulletElement: 'li',
        hideOnClick: false,
        clickable: true,
      },
    }),
    [banners.length],
  );

  if (!banners.length) return null;

  const updateHeight = () => {
    window.requestAnimationFrame(() => {
      swiperRef.current?.swiper?.update?.();
      swiperRef.current?.swiper?.updateAutoHeight?.(0);
      onImageReady?.();
    });
  };

  return (
    <AwesomeSwiper
      className={`m-swiper ${className}`.trim()}
      ref={(instance) => {
        swiperRef.current = instance as SwiperHandle | null;
      }}
      config={config}
    >
      <div className="swiper-wrapper">
        {banners.map((banner, index) => {
          const safeLink = getSafeLandingLink(banner.value);
          return (
            <img
              key={`${banner.label}-${index}`}
              className="swiper-slide pic-size"
              src={banner.label}
              alt={`轮播图${index + 1}`}
              onLoad={updateHeight}
              onError={updateHeight}
              onClick={() => {
                if (safeLink) window.open(safeLink, '_blank', 'noopener,noreferrer');
              }}
            />
          );
        })}
      </div>
      <div className={`swiper-pagination dot-block ${paginationClassRef.current}`}></div>
    </AwesomeSwiper>
  );
}

function hasSameImages(left: Props['images'] = [], right: Props['images'] = []): boolean {
  if (left.length !== right.length) return false;
  return left.every((item, index) => {
    const leftBanner = toBannerItem(item);
    const rightBanner = toBannerItem(right[index]);
    return leftBanner.label === rightBanner.label && leftBanner.value === rightBanner.value;
  });
}

export default memo(
  LandingCarousel,
  (previous, next) => previous.className === next.className
    && previous.onImageReady === next.onImageReady
    && hasSameImages(previous.images, next.images),
);
